import { IdType } from '../services/api';

/**
 * Formats a Costa Rican national ID for display with proper dash insertion
 * @param normalizedId - The normalized national ID (digits only)
 * @param idType - The type of ID (PersonalId or CompanyId)
 * @returns Formatted national ID string with dashes for readability
 */
export function formatNationalId(normalizedId: string, idType: IdType): string {
  if (!normalizedId) {
    return '';
  }

  // Remove any existing dashes and ensure we have only digits
  const digitsOnly = normalizedId.replace(/\D/g, '');

  if (idType === IdType.PersonalId) {
    // Personal ID: 9 digits formatted as X-XXXX-XXXX
    if (digitsOnly.length === 9) {
      return `${digitsOnly.slice(0, 1)}-${digitsOnly.slice(1, 5)}-${digitsOnly.slice(5, 9)}`;
    }
    // If not exactly 9 digits, return as-is (invalid format)
    return digitsOnly;
  } else if (idType === IdType.CompanyId) {
    // Company ID: 10 digits formatted as X-XXX-XXXXXX
    if (digitsOnly.length === 10) {
      return `${digitsOnly.slice(0, 1)}-${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 10)}`;
    }
    // If not exactly 10 digits, return as-is (invalid format)
    return digitsOnly;
  } else if (idType === IdType.DIMEX) {
    // DIMEX ID: 12 digits formatted as XXXX-XXXXXX-XX
    if (digitsOnly.length === 12) {
      return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 10)}-${digitsOnly.slice(10, 12)}`;
    }
    // If not exactly 12 digits, return as-is (invalid format)
    return digitsOnly;
  }

  // Fallback: return digits only if unknown type
  return digitsOnly;
}

/**
 * Formats a national ID for display with fallback to the ID itself when name is not available
 * This is used in UI components when resolved names are not available
 * @param normalizedId - The normalized national ID (digits only)
 * @param idType - The type of ID (PersonalId or CompanyId)
 * @param prefix - Optional prefix to add before the formatted ID (e.g., "ID: ")
 * @returns Formatted display string
 */
export function formatNationalIdForDisplay(
  normalizedId: string, 
  idType: IdType, 
  prefix: string = ''
): string {
  const formattedId = formatNationalId(normalizedId, idType);
  return prefix ? `${prefix}${formattedId}` : formattedId;
}